import React, { useState, useEffect } from 'react';
import { feedFormulasAPI, rawMaterialsAPI, customersAPI } from '../services/api';
import { MobileCard } from '../components/MobileCard';
import '../components/MobileCard.css';
import './FeedFormulas.css';

// --- Phase 3: live nutrition panel (client-side; nutrient values already in the rawMaterials payload) ---
const NUTRIENT_GROUPS: { label: string; unit: string; pct: boolean; fields: string[] }[] = [
  { label: 'Macro nutrients', unit: '%', pct: true,
    fields: ['protein', 'lysine', 'methionine', 'crude_fat', 'adf_fiber', 'ndf_fiber', 'calcium_min',
             'calcium_max', 'phosphorus', 'salt_min', 'salt_max', 'magnesium', 'sulfur', 'potassium'] },
  { label: 'Trace minerals', unit: 'PPM', pct: false,
    fields: ['selenium', 'zinc', 'copper', 'iron', 'manganese', 'aluminum', 'niacin', 'choline'] },
  { label: 'Energy', unit: 'Kcal/lb', pct: false, fields: ['energy_kcal_lb'] },
  { label: 'Vitamins', unit: 'IU/lb', pct: false, fields: ['vitamin_a', 'vitamin_d3', 'vitamin_e'] },
];
const ALL_NUTRIENTS: string[] = NUTRIENT_GROUPS.flatMap(g => g.fields);
const NUTRIENT_LABELS: Record<string, string> = {
  protein: 'Protein', lysine: 'Lysine', methionine: 'Methionine', crude_fat: 'Crude Fat', adf_fiber: 'ADF Fiber',
  ndf_fiber: 'NDF Fiber', calcium_min: 'Calcium min', calcium_max: 'Calcium max', phosphorus: 'Phosphorus',
  salt_min: 'Salt min', salt_max: 'Salt max', magnesium: 'Magnesium', sulfur: 'Sulfur', potassium: 'Potassium',
  selenium: 'Selenium', zinc: 'Zinc', copper: 'Copper', iron: 'Iron', manganese: 'Manganese', aluminum: 'Aluminum',
  niacin: 'Niacin', choline: 'Choline', energy_kcal_lb: 'Kcal/lb', vitamin_a: 'Vit A', vitamin_d3: 'Vit D3',
  vitamin_e: 'Vit E',
};

// Weighted nutrient roll-up from the live ingredient rows. null nutrient = "no data" (NOT zero):
// it never adds to a total, and any contributing ingredient missing a field flags that nutrient partial.
type NutriIngredient = { raw_material: number | string; percentage: number | string; raw_material_name?: string };
function computeNutrition(ingredients: NutriIngredient[], rawMaterials: RawMaterial[]) {
  const byId = new Map<number, any>();
  rawMaterials.forEach(r => byId.set(Number(r.id), r));
  const totals: Record<string, number> = {};
  const partial: Record<string, boolean> = {};
  ALL_NUTRIENTS.forEach(f => { totals[f] = 0; partial[f] = false; });
  const missing: string[] = [];
  let contributing = 0;
  (ingredients || []).forEach(ing => {
    if (!ing.raw_material) return;
    const pct = parseFloat(String(ing.percentage)) || 0;
    const rm = byId.get(Number(ing.raw_material));
    if (!rm) return;
    contributing += 1;
    const anyData = ALL_NUTRIENTS.some(f => rm[f] != null);
    if (!anyData) {
      // Ingredient has NO nutrient data at all (e.g. Swine VTM): its full contribution is unknown,
      // so it goes on the incomplete-ingredients worklist and makes every nutrient total provisional.
      missing.push(rm.name || ing.raw_material_name || `#${ing.raw_material}`);
      if (pct > 0) ALL_NUTRIENTS.forEach(f => { partial[f] = true; });
      return;
    }
    // Characterized ingredient: a null on one field = ~0 for that nutrient (a methionine supplement
    // has no protein), NOT "unknown" — so it does not flag partial. Only all-blank ingredients do.
    ALL_NUTRIENTS.forEach(f => {
      const v = rm[f];
      if (v != null) totals[f] += Number(v) * (pct / 100);
    });
  });
  return { totals, partial, missing, contributing };
}

// Shared read-only nutrition panel — rendered identically in the edit form and the View screen.
function NutritionPanel({ nutrition }: { nutrition: ReturnType<typeof computeNutrition> }) {
  if (!nutrition || nutrition.contributing === 0) return null;
  return (
    <div className="nutrition-panel" style={{ marginTop: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Nutrition</div>
      {NUTRIENT_GROUPS.map(group => (
        <div key={group.label} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>
            {group.label} <span style={{ opacity: 0.7 }}>({group.unit})</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 4 }}>
            {group.fields.map(f => {
              const val = nutrition.totals[f] || 0;
              const shown = group.pct ? `${(val * 100).toFixed(2)}%` : val.toFixed(2);
              return (
                <div key={f} style={{ fontSize: 13 }}>
                  {NUTRIENT_LABELS[f]}: <strong>{shown}</strong>
                  {nutrition.partial[f] && (
                    <span title="A contributing ingredient has no value for this nutrient — total is incomplete"
                      style={{ color: '#b45309' }}> ⚠ partial</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {nutrition.missing.length > 0 && (
        <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', borderRadius: 6, fontSize: 13 }}>
          ⚠ Incomplete data — these ingredients have no nutrient values entered:{' '}
          <strong>{nutrition.missing.join(', ')}</strong>
        </div>
      )}
    </div>
  );
}

interface RawMaterial {
  id: number;
  name: string;
  cost_per_unit: number;
  // Nutrient values ship in the payload (RawMaterialSerializer fields='__all__'); null = no data.
  protein?: number | null; lysine?: number | null; methionine?: number | null; crude_fat?: number | null;
  adf_fiber?: number | null; ndf_fiber?: number | null; calcium_min?: number | null; calcium_max?: number | null;
  phosphorus?: number | null; salt_min?: number | null; salt_max?: number | null; magnesium?: number | null;
  sulfur?: number | null; potassium?: number | null; selenium?: number | null; zinc?: number | null;
  copper?: number | null; iron?: number | null; manganese?: number | null; aluminum?: number | null;
  niacin?: number | null; choline?: number | null; energy_kcal_lb?: number | null; vitamin_a?: number | null;
  vitamin_d3?: number | null; vitamin_e?: number | null; lb_per_unit?: number | null;
}

interface Customer {
  id: number;
  name: string;
  customer_type: string;
}

interface EditIngredient {
  id?: number;
  raw_material: number | string;
  raw_material_name?: string;
  quantity: number;
  percentage: number;
  cost_per_lb?: number;
  notes?: string;
}

interface EditFormData {
  name: string;
  description: string;
  feed_grade: string;
  animal_type: string;
  default_form: string;
  available_forms: string[];
  bag_size: number | '';   // '' until someone enters it - formulas have no default bag size
  is_active: boolean;
  stable_cost_mash: number | null;
  stable_cost_pellet: number | null;
  // Availability settings
  is_custom_formula: boolean;
  default_available_wholesale: boolean;
  default_available_retail: boolean;
  // Production formula linking
  production_formula: number | null;
  default_bag_size: number | null;
  // Ingredients
  ingredients: EditIngredient[];
}

interface Ingredient {
  id: number;
  raw_material: number;
  raw_material_name: string;
  quantity: number;
  percentage: number;
  cost_per_lb: number;
}

interface FeedFormula {
  id: number;
  name: string;
  description: string;
  feed_grade: string | null;
  animal_type: string;
  stable_cost_per_lb: number | null;
  stable_cost_mash: number | null;
  stable_cost_pellet: number | null;
  bag_size: number;
  batch_size: number;
  price_bag_mash: number | null;
  price_bag_pellet: number | null;
  price_byoc_mash: number | null;
  price_byoc_pellet: number | null;
  price_1000lb_mash: number | null;
  price_1000lb_pellet: number | null;
  price_2000lb_mash: number | null;
  price_2000lb_pellet: number | null;
  default_form: string;
  available_forms: string[];
  is_active: boolean;
  is_custom_formula: boolean;
  default_available_wholesale: boolean;
  default_available_retail: boolean;
  production_formula: number | null;
  production_formula_name: string | null;
  default_bag_size: number | null;
  variants: { id: number; name: string }[];
  ingredients: Ingredient[];
}

const FeedFormulas: React.FC = () => {
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [filteredFormulas, setFilteredFormulas] = useState<FeedFormula[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedAnimal, setSelectedAnimal] = useState('');
  const [selectedFormula, setSelectedFormula] = useState<FeedFormula | null>(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Versioned editor: a saved-but-not-yet-activated DRAFT awaiting Brad's Activate click.
  const [pendingDraft, setPendingDraft] = useState<
    { versionId: number; versionNumber: number; sum: number; cost: number | null } | null
  >(null);
  const [notice, setNotice] = useState('');
  const [editFormData, setEditFormData] = useState<EditFormData>({
    name: '',
    description: '',
    feed_grade: '',
    animal_type: '',
    default_form: 'MASH',
    available_forms: [],
    bag_size: '',
    is_active: true,
    stable_cost_mash: null,
    stable_cost_pellet: null,
    is_custom_formula: false,
    default_available_wholesale: true,
    default_available_retail: false,
    production_formula: null,
    default_bag_size: null,
    ingredients: [],
  });

  // Create mode state
  const [isCreating, setIsCreating] = useState(false);

  // Raw materials and customers for dropdowns
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);

  const gradeOptions = [
    { value: '', label: 'All Grades' },
    { value: 'ESSENTIAL', label: 'Essential' },
    { value: 'PLUS', label: 'Plus' },
    { value: 'FIVE_STAR', label: 'Five Star' },
  ];

  const animalOptions = [
    { value: '', label: 'All Animals' },
    { value: 'TINY_DINO', label: 'Tiny Dino' },
    { value: 'POULTRY', label: 'Poultry' },
    { value: 'SWINE', label: 'Swine' },
    { value: 'RUMINANTS', label: 'Ruminants' },
    { value: 'SPECIAL', label: 'Special' },
    { value: 'HORSE', label: 'Horse' },
  ];

  useEffect(() => {
    loadFormulas();
    loadRawMaterials();
    loadCustomers();
  }, []);

  useEffect(() => {
    filterFormulas();
  }, [formulas, searchTerm, selectedGrade, selectedAnimal]);

  const loadRawMaterials = async () => {
    try {
      const response = await rawMaterialsAPI.getAll();
      const data = response.data?.results || response.data || [];
      setRawMaterials(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading raw materials:', err);
      setRawMaterials([]);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      const data = response.data?.results || response.data || [];
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading customers:', err);
      setCustomers([]);
    }
  };

  const loadFormulas = async () => {
    try {
      setLoading(true);
      const response = await feedFormulasAPI.getAll();
      const formulaData = response.data.results || response.data;
      // Filter only active formulas
      const activeFormulas = formulaData.filter((f: FeedFormula) => f.is_active);
      setFormulas(activeFormulas);
      setFilteredFormulas(activeFormulas);
    } catch (err) {
      console.error('Error loading feed formulas:', err);
      setError('Failed to load feed formulas');
    } finally {
      setLoading(false);
    }
  };

  const filterFormulas = () => {
    let filtered = [...formulas];

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (formula) =>
          formula.name.toLowerCase().includes(term) ||
          formula.description?.toLowerCase().includes(term)
      );
    }

    // Filter by grade
    if (selectedGrade) {
      filtered = filtered.filter((formula) => formula.feed_grade === selectedGrade);
    }

    // Filter by animal type
    if (selectedAnimal) {
      filtered = filtered.filter((formula) => formula.animal_type === selectedAnimal);
    }

    setFilteredFormulas(filtered);
  };

  const formatGrade = (grade: string | null) => {
    if (!grade) return 'Ungraded';   // nobody chose one; say so rather than show a guess
    if (grade === 'FIVE_STAR') return 'Five Star';
    return grade.charAt(0) + grade.slice(1).toLowerCase();
  };

  const formatAnimalType = (type: string) => {
    if (type === 'TINY_DINO') return 'Tiny Dino';
    return type.charAt(0) + type.slice(1).toLowerCase();
  };

  const formatCurrency = (amount: number | null | undefined, placeholder = 'Not set') => {
    if (amount == null || isNaN(amount)) return placeholder;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(amount);
  };

  // Calculate total percentage of ingredients
  const totalPercentage = (editFormData.ingredients || []).reduce(
    (sum, ing) => sum + (parseFloat(String(ing.percentage)) || 0),
    0
  );

  // Live nutrition profile — recomputes every render from the ingredient rows × rawMaterials payload.
  const nutrition = computeNutrition(editFormData.ingredients || [], rawMaterials);

  // Read-only View screen: same derivations from the selected formula's active_version ingredients.
  const viewIngredients = (selectedFormula?.ingredients || []) as any[];
  const viewNutrition = computeNutrition(viewIngredients, rawMaterials);
  const viewTotal = viewIngredients.reduce((s, i) => s + (parseFloat(String(i.percentage)) || 0), 0);

  // Edit mode functions
  const handleEnterEditMode = () => {
    if (!selectedFormula) return;
    try {
      // Safely map ingredients with null safety on each field
      const mappedIngredients = Array.isArray(selectedFormula.ingredients)
        ? selectedFormula.ingredients.map(ing => ({
            id: ing?.id,
            raw_material: ing?.raw_material || '',
            raw_material_name: ing?.raw_material_name || '',
            quantity: ing?.quantity || 0,
            percentage: ing?.percentage || 0,
            cost_per_lb: ing?.cost_per_lb || 0,
          }))
        : [];

      setEditFormData({
        name: selectedFormula.name || '',
        description: selectedFormula.description || '',
        feed_grade: selectedFormula.feed_grade || '',   // blank = ungraded; never guess a grade
        animal_type: selectedFormula.animal_type || 'POULTRY',
        default_form: selectedFormula.default_form || 'MASH',
        available_forms: Array.isArray(selectedFormula.available_forms) ? selectedFormula.available_forms : ['MASH'],
        bag_size: selectedFormula.bag_size ?? '',
        is_active: selectedFormula.is_active ?? true,
        stable_cost_mash: selectedFormula.stable_cost_mash ?? null,
        stable_cost_pellet: selectedFormula.stable_cost_pellet ?? null,
        is_custom_formula: selectedFormula.is_custom_formula || false,
        default_available_wholesale: selectedFormula.default_available_wholesale ?? true,
        default_available_retail: selectedFormula.default_available_retail ?? false,
        production_formula: selectedFormula.production_formula ?? null,
        default_bag_size: selectedFormula.default_bag_size ?? null,
        ingredients: mappedIngredients,
      });
      setIsEditing(true);
    } catch (error) {
      console.error('Error entering edit mode:', error);
      setError('Failed to enter edit mode');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setError('');
  };

  const handleSaveFormula = async () => {
    if (!selectedFormula && !isCreating) return;
    // No default bag size: a guessed 40 is how the 50 lb Essentials line came to claim 40.
    if (!editFormData.feed_grade) {
      setError('Choose a feed grade - there is no default.');
      return;
    }
    if (!(Number(editFormData.bag_size) > 0)) {
      setError('Enter the bag size in pounds - there is no default.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      // Ingredient edits flow through the versioned save_draft endpoint (NOT the old table).
      const ingredients = editFormData.ingredients
        .filter(ing => ing.raw_material && Number(ing.percentage) > 0)
        .map((ing, idx) => ({
          raw_material: parseInt(String(ing.raw_material)),
          percentage: parseFloat(String(ing.percentage)),
          quantity: ing.quantity || (selectedFormula?.batch_size || 2000) * (Number(ing.percentage) / 100),
          sort_order: idx,
        }));

      // Metadata only — names render live on invoices; this never writes ingredients.
      const metadata = {
        name: editFormData.name,
        description: editFormData.description,
        feed_grade: editFormData.feed_grade,
        animal_type: editFormData.animal_type,
        default_form: editFormData.default_form,
        available_forms: editFormData.available_forms,
        bag_size: editFormData.bag_size,
        is_active: editFormData.is_active,
        stable_cost_mash: editFormData.stable_cost_mash,
        stable_cost_pellet: editFormData.stable_cost_pellet,
        is_custom_formula: editFormData.is_custom_formula,
        default_available_wholesale: editFormData.default_available_wholesale,
        default_available_retail: editFormData.default_available_retail,
        production_formula: editFormData.production_formula || null,
        default_bag_size: editFormData.default_bag_size || null,
      };

      if (isCreating) {
        // New formula: create metadata, save first draft, activate immediately (no prior to preview).
        const created = await feedFormulasAPI.create(metadata);
        if (ingredients.length > 0) {
          const draft = await feedFormulasAPI.saveDraft(created.data.id, { ingredients });
          await feedFormulasAPI.activateVersion(created.data.id, draft.data.version_id);
        }
        await loadFormulas();
        setIsCreating(false);
        setIsEditing(false);
      } else if (selectedFormula) {
        // Edit: metadata saves live; ingredients become a DRAFT to preview, then Activate.
        await feedFormulasAPI.partialUpdate(selectedFormula.id, metadata);
        if (ingredients.length > 0) {
          const draft = await feedFormulasAPI.saveDraft(selectedFormula.id, { ingredients });
          setPendingDraft({
            versionId: draft.data.version_id,
            versionNumber: draft.data.version_number,
            sum: draft.data.percentage_sum,
            cost: draft.data.cost_per_lb,
          });
        } else {
          setIsEditing(false);
          await loadFormulas();
        }
      }
    } catch (err: any) {
      console.error('Error saving formula:', err);
      let errorMessage = 'Failed to save formula';
      if (err.response?.data) {
        const errorData = err.response.data;
        if (typeof errorData === 'object') {
          const fieldErrors = Object.entries(errorData)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          errorMessage = fieldErrors || errorMessage;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivateDraft = async () => {
    if (!pendingDraft || !selectedFormula) return;
    setIsSaving(true);
    setError('');
    try {
      const resp = await feedFormulasAPI.activateVersion(selectedFormula.id, pendingDraft.versionId);
      setNotice(resp.data?.warning || '');  // advisory only; activation already succeeded
      setPendingDraft(null);
      setIsEditing(false);
      const fresh = await feedFormulasAPI.getById(selectedFormula.id);
      setSelectedFormula(fresh.data);
      await loadFormulas();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to activate version');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!pendingDraft || !selectedFormula) return;
    setIsSaving(true);
    try {
      await feedFormulasAPI.discardDraft(selectedFormula.id, pendingDraft.versionId);
      setPendingDraft(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to discard draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvailableFormsChange = (form: string) => {
    setEditFormData(prev => {
      const currentForms = prev.available_forms || [];
      const forms = currentForms.includes(form)
        ? currentForms.filter(f => f !== form)
        : [...currentForms, form];
      return { ...prev, available_forms: forms };
    });
  };

  // Ingredient editing functions
  const handleIngredientChange = (index: number, field: string, value: any) => {
    try {
      const currentIngredients = editFormData.ingredients || [];
      const updatedIngredients = [...currentIngredients];

      // Make sure the index exists
      if (!updatedIngredients[index]) {
        updatedIngredients[index] = { raw_material: '', percentage: 0, quantity: 0 };
      }

      updatedIngredients[index] = {
        ...updatedIngredients[index],
        [field]: value,
      };

      // If changing raw material, update cost_per_lb and name
      if (field === 'raw_material' && value) {
        const rmId = parseInt(String(value));
        if (!isNaN(rmId) && Array.isArray(rawMaterials)) {
          const selectedRM = rawMaterials.find(rm => rm.id === rmId);
          if (selectedRM) {
            updatedIngredients[index].raw_material_name = selectedRM.name || '';
            updatedIngredients[index].cost_per_lb = selectedRM.cost_per_unit || 0;
          }
        }
      }

      setEditFormData({ ...editFormData, ingredients: updatedIngredients });
    } catch (error) {
      console.error('Error in handleIngredientChange:', error);
    }
  };

  const handleAddIngredient = () => {
    try {
      const currentIngredients = editFormData.ingredients || [];
      setEditFormData({
        ...editFormData,
        ingredients: [
          ...currentIngredients,
          { raw_material: '', percentage: 0, quantity: 0, cost_per_lb: 0 }
        ]
      });
    } catch (error) {
      console.error('Error in handleAddIngredient:', error);
    }
  };

  const handleRemoveIngredient = (index: number) => {
    try {
      const currentIngredients = editFormData.ingredients || [];
      const updatedIngredients = currentIngredients.filter((_, i) => i !== index);
      setEditFormData({ ...editFormData, ingredients: updatedIngredients });
    } catch (error) {
      console.error('Error in handleRemoveIngredient:', error);
    }
  };

  // New formula creation
  const handleStartCreate = () => {
    setEditFormData({
      name: '',
      description: '',
      feed_grade: '',
      animal_type: 'POULTRY',
      default_form: 'MASH',
      available_forms: ['MASH'],
      bag_size: '',
      is_active: true,
      stable_cost_mash: null,
      stable_cost_pellet: null,
      is_custom_formula: false,
      default_available_wholesale: true,
      default_available_retail: false,
      production_formula: null,
      default_bag_size: null,
      ingredients: [],
    });
    setIsCreating(true);
    setSelectedFormula(null);
  };

  const handleCloseDetail = () => {
    setSelectedFormula(null);
    setIsEditing(false);
    setError('');
  };

  if (loading) {
    return (
      <div className="formulas-page">
        <div className="loading">Loading feed formulas...</div>
      </div>
    );
  }

  return (
    <div className="formulas-page">
      <div className="page-header">
        <div>
          <h1>Feed Formulas</h1>
          <p className="page-subtitle">Red Bridge Farm Feed Co.</p>
        </div>
        <button className="btn-new-formula" onClick={handleStartCreate}>
          + New Formula
        </button>
      </div>

      {error && !selectedFormula && !isCreating && <div className="alert alert-error">{error}</div>}

      <div className="formulas-container">
        {/* Filters */}
        <div className="filters-section">
          <div className="filter-row">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search formulas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="filter-select"
              >
                {gradeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <select
                value={selectedAnimal}
                onChange={(e) => setSelectedAnimal(e.target.value)}
                className="filter-select"
              >
                {animalOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="results-count">
              {filteredFormulas.length} formula{filteredFormulas.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Formulas Table */}
        <div className="desktop-table-container">
          <table className="formulas-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Grade</th>
                <th>Animal Type</th>
                <th>Forms Available</th>
                <th>Cost/lb</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFormulas.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-data">
                    No formulas found
                  </td>
                </tr>
              ) : (
                filteredFormulas.map((formula) => (
                  <tr key={formula.id} className="formula-row" onClick={() => setSelectedFormula(formula)}>
                    <td className="formula-name">{formula.name}</td>
                    <td>
                      <span className={`grade-badge grade-${(formula.feed_grade || 'none').toLowerCase()}`}>
                        {formatGrade(formula.feed_grade)}
                      </span>
                    </td>
                    <td>{formatAnimalType(formula.animal_type)}</td>
                    <td>
                      <div className="forms-list">
                        {formula.available_forms && formula.available_forms.length > 0
                          ? formula.available_forms.join(', ')
                          : 'Both'}
                      </div>
                    </td>
                    <td className="cost-column">{formatCurrency(formula.stable_cost_per_lb)}</td>
                    <td>
                      <button
                        className="btn-view"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFormula(formula);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards (shown on small screens) */}
        <div className="mobile-cards-container">
          <div className="mobile-card-list">
            {filteredFormulas.length === 0 ? (
              <div className="no-data-card">No formulas found</div>
            ) : (
              filteredFormulas.map((formula) => (
                <MobileCard
                  key={formula.id}
                  title={formula.name}
                  subtitle={formatAnimalType(formula.animal_type)}
                  badge={
                    <span className={`grade-badge grade-${(formula.feed_grade || 'none').toLowerCase()}`}>
                      {formatGrade(formula.feed_grade)}
                    </span>
                  }
                  fields={[
                    { label: 'Forms', value: formula.available_forms?.join(', ') || 'Both' },
                    { label: 'Cost/lb', value: formatCurrency(formula.stable_cost_per_lb) },
                  ]}
                  actions={
                    <button className="btn btn-primary btn-sm" onClick={() => setSelectedFormula(formula)}>
                      View Details
                    </button>
                  }
                  onClick={() => setSelectedFormula(formula)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Formula Detail Full-Screen View */}
      {selectedFormula && (
        <div className="formula-fullscreen-view">
          <div className="formula-view-header">
            <div className="formula-header-info">
              <h2>{selectedFormula.name}</h2>
              <span className={`grade-badge grade-${(selectedFormula.feed_grade || 'none').toLowerCase()}`}>
                {formatGrade(selectedFormula.feed_grade)}
              </span>
            </div>
            <div className="formula-header-actions">
              {isEditing ? (
                <>
                  <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit} disabled={isSaving}>
                    Cancel
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveFormula} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Draft'}
                  </button>
                  {pendingDraft && (
                    <span style={{ marginLeft: 10, fontSize: 13 }}>
                      Draft v{pendingDraft.versionNumber} saved (sum {pendingDraft.sum}%
                      {pendingDraft.cost != null ? `, $${pendingDraft.cost}/lb` : ''}) —
                      <button className="btn btn-success btn-sm" onClick={handleActivateDraft}
                        disabled={isSaving} style={{ marginLeft: 6 }}>
                        Activate
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={handleDiscardDraft}
                        disabled={isSaving} style={{ marginLeft: 4 }}>
                        Discard
                      </button>
                    </span>
                  )}
                </>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={handleEnterEditMode}>
                  Edit
                </button>
              )}
              <button className="formula-view-close" onClick={handleCloseDetail}>
                ✕
              </button>
            </div>
          </div>

          {error && <div className="alert alert-error formula-error">{error}</div>}
          {notice && (
            <div className="alert alert-warning formula-error" onClick={() => setNotice('')}>
              ⚠ {notice} <span style={{ opacity: 0.6 }}>(click to dismiss)</span>
            </div>
          )}

          <div className="formula-view-content">
            {isEditing ? (
              /* Edit Mode */
              <div className="formula-edit-form">
                <div className="form-group">
                  <label>Formula Name</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Animal Type</label>
                  <select
                    value={editFormData.animal_type}
                    onChange={(e) => setEditFormData({ ...editFormData, animal_type: e.target.value })}
                  >
                    <option value="POULTRY">Poultry</option>
                    <option value="SWINE">Swine</option>
                    <option value="HORSE">Horse</option>
                    <option value="RUMINANTS">Ruminants</option>
                    <option value="SPECIAL">Special</option>
                    <option value="TINY_DINO">Tiny Dino</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Grade</label>
                  <select
                    value={editFormData.feed_grade}
                    onChange={(e) => setEditFormData({ ...editFormData, feed_grade: e.target.value })}
                  >
                    <option value="ESSENTIAL">Essential</option>
                    <option value="PLUS">Plus</option>
                    <option value="FIVE_STAR">Five Star</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Default Form</label>
                  <select
                    value={editFormData.default_form}
                    onChange={(e) => setEditFormData({ ...editFormData, default_form: e.target.value })}
                  >
                    <option value="MASH">Mash</option>
                    <option value="PELLET">Pellet</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Available Forms</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={editFormData.available_forms?.includes('MASH') ?? false}
                        onChange={() => handleAvailableFormsChange('MASH')}
                      />
                      Mash
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={editFormData.available_forms?.includes('PELLET') ?? false}
                        onChange={() => handleAvailableFormsChange('PELLET')}
                      />
                      Pellet
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Bag Size (lbs)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editFormData.bag_size}
                    onChange={(e) => setEditFormData({ ...editFormData, bag_size: e.target.value as any })}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      setEditFormData({ ...editFormData, bag_size: !isNaN(val) && val > 0 ? val : '' });
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Production Formula</label>
                  <select
                    value={editFormData.production_formula ?? ''}
                    onChange={(e) => setEditFormData({ ...editFormData, production_formula: e.target.value ? parseInt(e.target.value) : null })}
                  >
                    <option value="">None (standalone formula)</option>
                    {formulas
                      .filter(f => f.is_active && f.id !== selectedFormula?.id)
                      .map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))
                    }
                  </select>
                  {editFormData.production_formula && (
                    <div style={{ marginTop: '6px', padding: '8px 12px', background: '#e8f4fd', borderRadius: '4px', fontSize: '13px', color: '#1a5276' }}>
                      Variant of <strong>{formulas.find(f => f.id === editFormData.production_formula)?.name}</strong>. Same recipe, batched together in production.
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Default Bag Size (lbs)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editFormData.default_bag_size ?? ''}
                    onChange={(e) => setEditFormData({ ...editFormData, default_bag_size: e.target.value as any })}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      setEditFormData({ ...editFormData, default_bag_size: !isNaN(val) && val > 0 ? val : null });
                    }}
                    placeholder="Optional — for order entry"
                  />
                </div>

                <div className="form-group">
                  <label>Stable Cost - Mash ($/lb)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={editFormData.stable_cost_mash ?? ''}
                    onChange={(e) => setEditFormData({ ...editFormData, stable_cost_mash: e.target.value as any })}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      setEditFormData({ ...editFormData, stable_cost_mash: !isNaN(val) && val >= 0 ? val : null });
                    }}
                    placeholder="Enter cost per lb"
                  />
                </div>

                <div className="form-group">
                  <label>Stable Cost - Pellet ($/lb)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={editFormData.stable_cost_pellet ?? ''}
                    onChange={(e) => setEditFormData({ ...editFormData, stable_cost_pellet: e.target.value as any })}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      setEditFormData({ ...editFormData, stable_cost_pellet: !isNaN(val) && val >= 0 ? val : null });
                    }}
                    placeholder="Enter cost per lb"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    placeholder="Formula notes..."
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label inline">
                    <input
                      type="checkbox"
                      checked={editFormData.is_active}
                      onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>

                {/* Customer Availability Section */}
                <div className="form-section">
                  <h3>Customer Availability</h3>
                  <p className="section-description">Who can order this formula?</p>

                  <div className="availability-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="availability"
                        checked={!editFormData.is_custom_formula && editFormData.default_available_wholesale && editFormData.default_available_retail}
                        onChange={() => setEditFormData({
                          ...editFormData,
                          is_custom_formula: false,
                          default_available_wholesale: true,
                          default_available_retail: true,
                        })}
                      />
                      <div className="radio-content">
                        <span className="radio-title">All Customers</span>
                        <span className="radio-description">Available to everyone by default</span>
                      </div>
                    </label>

                    <label className="radio-option">
                      <input
                        type="radio"
                        name="availability"
                        checked={!editFormData.is_custom_formula && editFormData.default_available_wholesale && !editFormData.default_available_retail}
                        onChange={() => setEditFormData({
                          ...editFormData,
                          is_custom_formula: false,
                          default_available_wholesale: true,
                          default_available_retail: false,
                        })}
                      />
                      <div className="radio-content">
                        <span className="radio-title">Wholesale Farms Only</span>
                        <span className="radio-description">Only available to wholesale farm customers</span>
                      </div>
                    </label>

                    <label className="radio-option">
                      <input
                        type="radio"
                        name="availability"
                        checked={!editFormData.is_custom_formula && !editFormData.default_available_wholesale && editFormData.default_available_retail}
                        onChange={() => setEditFormData({
                          ...editFormData,
                          is_custom_formula: false,
                          default_available_wholesale: false,
                          default_available_retail: true,
                        })}
                      />
                      <div className="radio-content">
                        <span className="radio-title">Retail Partners Only</span>
                        <span className="radio-description">Only available to retail partner customers</span>
                      </div>
                    </label>

                    <label className="radio-option">
                      <input
                        type="radio"
                        name="availability"
                        checked={editFormData.is_custom_formula}
                        onChange={() => setEditFormData({
                          ...editFormData,
                          is_custom_formula: true,
                          default_available_wholesale: false,
                          default_available_retail: false,
                        })}
                      />
                      <div className="radio-content">
                        <span className="radio-title">Custom Formula</span>
                        <span className="radio-description">Only available to specifically assigned customers</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Ingredients Section */}
                <div className="form-section">
                  <h3>Ingredients</h3>

                  <div className="ingredients-edit">
                    <div className="ingredient-header-row">
                      <span>Ingredient</span>
                      <span>Percentage</span>
                      <span>Cost/lb</span>
                      <span></span>
                    </div>

                    {(editFormData.ingredients || []).map((ingredient, index) => (
                      <div key={index} className="ingredient-edit-row">
                        <select
                          value={ingredient?.raw_material || ''}
                          onChange={(e) => handleIngredientChange(index, 'raw_material', e.target.value)}
                        >
                          <option value="">Select ingredient...</option>
                          {(rawMaterials || []).map(rm => (
                            <option key={rm.id} value={rm.id}>{rm.name || 'Unknown'}</option>
                          ))}
                        </select>

                        <input
                          type="text"
                          inputMode="decimal"
                          value={ingredient?.percentage ?? ''}
                          onChange={(e) => handleIngredientChange(index, 'percentage', e.target.value)}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            handleIngredientChange(index, 'percentage', !isNaN(val) && val >= 0 ? Math.min(100, val) : 0);
                          }}
                          className="percentage-input"
                        />

                        <span className="cost-display">
                          ${(parseFloat(String(ingredient?.cost_per_lb)) || 0).toFixed(4)}</span>

                        <button
                          type="button"
                          onClick={() => handleRemoveIngredient(index)}
                          className="btn-remove-ingredient"
                          title="Remove ingredient"
                        >
                          ✕
                        </button>
                      </div>
                    ))}

                    <button type="button" onClick={handleAddIngredient} className="btn-add-ingredient">
                      + Add Ingredient
                    </button>

                    <div className={`total-percentage ${Math.abs(totalPercentage - 100) < 0.01 ? 'valid' : 'warning'}`}>
                      Total: {totalPercentage.toFixed(2)}%
                      {Math.abs(totalPercentage - 100) >= 0.01 && (
                        <span className="warning-text"> (should be 100%)</span>
                      )}
                    </div>

                    {/* Phase 3: live nutrition (shared read-only panel; recomputes as you type) */}
                    <NutritionPanel nutrition={nutrition} />
                  </div>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                <div className="detail-section">
                  <h3>Formula Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Animal Type:</label>
                      <span>{formatAnimalType(selectedFormula.animal_type)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Stable Cost (Mash):</label>
                      <span className="cost-value">{formatCurrency(selectedFormula.stable_cost_mash)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Stable Cost (Pellet):</label>
                      <span className="cost-value">{formatCurrency(selectedFormula.stable_cost_pellet)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Bag Size:</label>
                      <span>{selectedFormula.bag_size} lbs</span>
                    </div>
                    <div className="detail-item">
                      <label>Default Form:</label>
                      <span>{selectedFormula.default_form}</span>
                    </div>
                    <div className="detail-item">
                      <label>Available Forms:</label>
                      <span>
                        {selectedFormula.available_forms && selectedFormula.available_forms.length > 0
                          ? selectedFormula.available_forms.join(', ')
                          : 'Both'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Status:</label>
                      <span className={selectedFormula.is_active ? 'status-active' : 'status-inactive'}>
                        {selectedFormula.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {selectedFormula.production_formula_name && (
                      <div className="detail-item">
                        <label>Production Variant Of:</label>
                        <span>{selectedFormula.production_formula_name}</span>
                      </div>
                    )}
                    {selectedFormula.default_bag_size && (
                      <div className="detail-item">
                        <label>Default Bag Size:</label>
                        <span>{selectedFormula.default_bag_size} lbs</span>
                      </div>
                    )}
                    {selectedFormula.variants && selectedFormula.variants.length > 0 && (
                      <div className="detail-item">
                        <label>Variants:</label>
                        <span>{selectedFormula.variants.map(v => v.name).join(', ')}</span>
                      </div>
                    )}
                  </div>
                  {selectedFormula.description && (
                    <div className="description-block">{selectedFormula.description}</div>
                  )}
                </div>

                <div className="detail-section">
                  <h3>Pricing</h3>
                  <div className="pricing-grid">
                    <div className="pricing-item">
                      <label>Bag (Mash):</label>
                      <span>{formatCurrency(selectedFormula.price_bag_mash)}</span>
                    </div>
                    <div className="pricing-item">
                      <label>Bag (Pellet):</label>
                      <span>{formatCurrency(selectedFormula.price_bag_pellet)}</span>
                    </div>
                    <div className="pricing-item">
                      <label>BYOC (Mash):</label>
                      <span>{formatCurrency(selectedFormula.price_byoc_mash)}</span>
                    </div>
                    <div className="pricing-item">
                      <label>BYOC (Pellet):</label>
                      <span>{formatCurrency(selectedFormula.price_byoc_pellet)}</span>
                    </div>
                    <div className="pricing-item">
                      <label>1000lb (Mash):</label>
                      <span>{formatCurrency(selectedFormula.price_1000lb_mash)}</span>
                    </div>
                    <div className="pricing-item">
                      <label>1000lb (Pellet):</label>
                      <span>{formatCurrency(selectedFormula.price_1000lb_pellet)}</span>
                    </div>
                    <div className="pricing-item">
                      <label>2000lb (Mash):</label>
                      <span>{formatCurrency(selectedFormula.price_2000lb_mash)}</span>
                    </div>
                    <div className="pricing-item">
                      <label>2000lb (Pellet):</label>
                      <span>{formatCurrency(selectedFormula.price_2000lb_pellet)}</span>
                    </div>
                  </div>
                </div>

                {selectedFormula.ingredients && selectedFormula.ingredients.length > 0 && (
                  <div className="detail-section">
                    <h3>Ingredients</h3>
                    <table className="ingredients-table">
                      <thead>
                        <tr>
                          <th>Ingredient</th>
                          <th>Percentage</th>
                          <th>Cost/lb</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedFormula.ingredients.map((ingredient) => (
                          <tr key={ingredient.id}>
                            <td>{ingredient.raw_material_name}</td>
                            <td>{ingredient.percentage}%</td>
                            <td>{formatCurrency(ingredient.cost_per_lb)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className={`total-percentage ${Math.abs(viewTotal - 100) < 0.01 ? 'valid' : 'warning'}`}>
                      Total: {viewTotal.toFixed(2)}%
                      {Math.abs(viewTotal - 100) >= 0.01 && (
                        <span className="warning-text"> (not 100%)</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Read-only nutrition for the active version (shared panel) */}
                <NutritionPanel nutrition={viewNutrition} />
              </>
            )}
          </div>
        </div>
      )}

      {/* New Formula Full-Screen View */}
      {isCreating && (
        <div className="formula-fullscreen-view">
          <div className="formula-view-header">
            <div className="formula-header-info">
              <h2>New Formula</h2>
            </div>
            <div className="formula-header-actions">
              <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit} disabled={isSaving}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSaveFormula} disabled={isSaving}>
                {isSaving ? 'Creating...' : 'Create Formula'}
              </button>
            </div>
          </div>

          {error && <div className="alert alert-error formula-error">{error}</div>}

          <div className="formula-view-content">
            <div className="formula-edit-form">
              {/* Basic Information */}
              <div className="form-section">
                <h3>Basic Information</h3>

                <div className="form-group">
                  <label>Formula Name *</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    placeholder="Enter formula name"
                  />
                </div>

                <div className="form-group">
                  <label>Animal Type</label>
                  <select
                    value={editFormData.animal_type}
                    onChange={(e) => setEditFormData({ ...editFormData, animal_type: e.target.value })}
                  >
                    <option value="POULTRY">Poultry</option>
                    <option value="SWINE">Swine</option>
                    <option value="HORSE">Horse</option>
                    <option value="RUMINANTS">Ruminants</option>
                    <option value="SPECIAL">Special</option>
                    <option value="TINY_DINO">Tiny Dino</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Grade</label>
                  <select
                    value={editFormData.feed_grade}
                    onChange={(e) => setEditFormData({ ...editFormData, feed_grade: e.target.value })}
                  >
                    <option value="ESSENTIAL">Essential</option>
                    <option value="PLUS">Plus</option>
                    <option value="FIVE_STAR">Five Star</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Default Form</label>
                  <select
                    value={editFormData.default_form}
                    onChange={(e) => setEditFormData({ ...editFormData, default_form: e.target.value })}
                  >
                    <option value="MASH">Mash</option>
                    <option value="PELLET">Pellet</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Available Forms</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={editFormData.available_forms?.includes('MASH') ?? false}
                        onChange={() => handleAvailableFormsChange('MASH')}
                      />
                      Mash
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={editFormData.available_forms?.includes('PELLET') ?? false}
                        onChange={() => handleAvailableFormsChange('PELLET')}
                      />
                      Pellet
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Bag Size (lbs)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editFormData.bag_size}
                    onChange={(e) => setEditFormData({ ...editFormData, bag_size: e.target.value as any })}
                    onBlur={(e) => {
                      const val = parseFloat(e.target.value);
                      setEditFormData({ ...editFormData, bag_size: !isNaN(val) && val > 0 ? val : '' });
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    placeholder="Formula notes..."
                  />
                </div>
              </div>

              {/* Customer Availability Section */}
              <div className="form-section">
                <h3>Customer Availability</h3>
                <p className="section-description">Who can order this formula?</p>

                <div className="availability-options">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="create-availability"
                      checked={!editFormData.is_custom_formula && editFormData.default_available_wholesale && editFormData.default_available_retail}
                      onChange={() => setEditFormData({
                        ...editFormData,
                        is_custom_formula: false,
                        default_available_wholesale: true,
                        default_available_retail: true,
                      })}
                    />
                    <div className="radio-content">
                      <span className="radio-title">All Customers</span>
                      <span className="radio-description">Available to everyone by default</span>
                    </div>
                  </label>

                  <label className="radio-option">
                    <input
                      type="radio"
                      name="create-availability"
                      checked={!editFormData.is_custom_formula && editFormData.default_available_wholesale && !editFormData.default_available_retail}
                      onChange={() => setEditFormData({
                        ...editFormData,
                        is_custom_formula: false,
                        default_available_wholesale: true,
                        default_available_retail: false,
                      })}
                    />
                    <div className="radio-content">
                      <span className="radio-title">Wholesale Farms Only</span>
                      <span className="radio-description">Only available to wholesale farm customers</span>
                    </div>
                  </label>

                  <label className="radio-option">
                    <input
                      type="radio"
                      name="create-availability"
                      checked={!editFormData.is_custom_formula && !editFormData.default_available_wholesale && editFormData.default_available_retail}
                      onChange={() => setEditFormData({
                        ...editFormData,
                        is_custom_formula: false,
                        default_available_wholesale: false,
                        default_available_retail: true,
                      })}
                    />
                    <div className="radio-content">
                      <span className="radio-title">Retail Partners Only</span>
                      <span className="radio-description">Only available to retail partner customers</span>
                    </div>
                  </label>

                  <label className="radio-option">
                    <input
                      type="radio"
                      name="create-availability"
                      checked={editFormData.is_custom_formula}
                      onChange={() => setEditFormData({
                        ...editFormData,
                        is_custom_formula: true,
                        default_available_wholesale: false,
                        default_available_retail: false,
                      })}
                    />
                    <div className="radio-content">
                      <span className="radio-title">Custom Formula</span>
                      <span className="radio-description">Only available to specifically assigned customers</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Ingredients Section */}
              <div className="form-section">
                <h3>Ingredients</h3>

                <div className="ingredients-edit">
                  <div className="ingredient-header-row">
                    <span>Ingredient</span>
                    <span>Percentage</span>
                    <span>Cost/lb</span>
                    <span></span>
                  </div>

                  {(editFormData.ingredients || []).map((ingredient, index) => (
                    <div key={index} className="ingredient-edit-row">
                      <select
                        value={ingredient?.raw_material || ''}
                        onChange={(e) => handleIngredientChange(index, 'raw_material', e.target.value)}
                      >
                        <option value="">Select ingredient...</option>
                        {(rawMaterials || []).map(rm => (
                          <option key={rm.id} value={rm.id}>{rm.name || 'Unknown'}</option>
                        ))}
                      </select>

                      <input
                        type="text"
                        inputMode="decimal"
                        value={ingredient?.percentage ?? ''}
                        onChange={(e) => handleIngredientChange(index, 'percentage', e.target.value)}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          handleIngredientChange(index, 'percentage', !isNaN(val) && val >= 0 ? Math.min(100, val) : 0);
                        }}
                        className="percentage-input"
                      />

                      <span className="cost-display">
                        ${(parseFloat(String(ingredient?.cost_per_lb)) || 0).toFixed(4)}
                      </span>

                      <button
                        type="button"
                        onClick={() => handleRemoveIngredient(index)}
                        className="btn-remove-ingredient"
                        title="Remove ingredient"
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <button type="button" onClick={handleAddIngredient} className="btn-add-ingredient">
                    + Add Ingredient
                  </button>

                  {(editFormData.ingredients || []).length > 0 && (
                    <div className={`total-percentage ${Math.abs(totalPercentage - 100) < 0.01 ? 'valid' : 'warning'}`}>
                      Total: {totalPercentage.toFixed(2)}%
                      {Math.abs(totalPercentage - 100) >= 0.01 && (
                        <span className="warning-text"> (should be 100%)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedFormulas;
